/* eslint-disable react/no-array-index-key */
import {SerializedStyles} from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import {Box, FormHelperText, IconButton, InputLabel, MenuItem, OutlinedInput, Select, css, useTheme} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import {GetStylesFunction, TranslateUtils, utils, DlMuiCaretDownIcon, DlMuiDynamicIcon} from '@wallaby-ui/core';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {FILTER_TYPE_MULTI_SELECT, FILTER_TYPE_SELECT} from 'src/js/lib/filtering';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig, useFormatters} from './LightFormatter';
import {ValidateError} from './LightValidator';

type Option = {
  id?: string | number;
  name?: string;
  label?: string;
  messageKey?: string;
};
type NormalizedOption = {id?: string; label?: string};
type Props = {
  config: FilterConfig;
  options: Option[];
  value: any;
  onChange?: (newValue: any) => void;
  multi?: boolean;
  getOptionLabel?: (option: NormalizedOption) => string;
  selectCss?: SerializedStyles;
  error?: ValidateError;
  isDefaultValue?: boolean;
};
// NOTE: LightSelectFilter2 uses mui select, whereas LightSelectFilter uses mui autocomplete
/** @deprecated TODO: why were we using this at all?, we shouldn't use multiple inputs that do the same thing, use `<LightSelectFilter>` instead */
export const LightSelectFilter2: React.FC<Props> = (props) => {
  const {
    config: _config,
    options: _options,
    value: _value,
    onChange,
    multi = false,
    getOptionLabel: _getOptionLabel,
    selectCss,
    error,
    isDefaultValue,
  } = props;
  const filterType = multi ? FILTER_TYPE_MULTI_SELECT : FILTER_TYPE_SELECT;
  const {t} = useFormatters();
  const config = {
    ..._config,
    ui: {
      label: t(_config.ui.label),
      placeholder: _config.ui.placeholder ? t(_config.ui.placeholder) : _config.ui.placeholder,
      type: filterType,
    },
  };
  const value = useMemo(() => (multi ? _value ?? [] : _value || null), [multi, _value]);
  const intl = useIntl();
  const options: NormalizedOption[] = useMemo(
    () =>
      (_options ?? []).map((option) => ({
        id: String(option.id ?? option.name),
        label: option.messageKey ? utils.TranslateUtils.translate(option.messageKey, intl) : option.label,
      })),
    [_options, intl]
  );
  const handleChange = useCallback(
    (event, newValue) => {
      if (onChange) {
        if (multi) onChange(newValue.length ? newValue.map((v) => v.id ?? v) : null);
        else onChange(newValue !== null ? newValue.id ?? newValue : null);
      }
    },
    [onChange, multi]
  );
  const theme = useTheme();

  const hasValue = multi ? value.length > 0 : value !== null;
  let isOccupied = !isDefaultValue;
  if (isDefaultValue === undefined) isOccupied = hasValue;
  const styles = getStyles({
    theme,
    params: {isOccupied, selectCss},
  });
  const getOptionLabel = _getOptionLabel ?? ((option) => option.label ?? '');
  const valueToRender = value ?? '';
  const translatedLabel = TranslateUtils.translate(config.ui.label, intl);
  const translatedPlaceholder = config.ui.placeholder ? TranslateUtils.translate(config.ui.placeholder, intl) : undefined;
  const chipOnDelete = useCallback(
    (id) => (event) => {
      event.preventDefault();
      handleChange(
        event,
        value.filter((v) => {
          return v !== id;
        })
      );
    },
    [handleChange, value]
  );
  return (
    <span
      className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')}
      css={styles.filterWrapper}
      data-name={config.name}
    >
      <FormControl css={styles.select} error={error?.bool}>
        <InputLabel size='small'>{translatedLabel}</InputLabel>
        <Select
          IconComponent={DlMuiCaretDownIcon}
          multiple={multi}
          value={valueToRender}
          onChange={(event) => handleChange(event, event.target.value)}
          input={
            <OutlinedInput
              label={translatedLabel}
              css={styles.textField}
              endAdornment={
                <IconButton
                  data-name='select-clearAllIconButton'
                  onClick={(event) => {
                    event.preventDefault();
                    handleChange(event, multi ? [] : null);
                  }}
                  css={styles.clearAllIconButton}
                  style={{visibility: hasValue ? undefined : 'hidden'}}
                >
                  <CloseIcon viewBox='0 0 24 24' css={styles.clearAllIcon} />
                </IconButton>
              }
            />
          }
          displayEmpty
          renderValue={(selected) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {multi ? (
                  <>
                    {(selected ?? []).slice(0, 3).map((id) => (
                      <div key={id} css={styles.selectChip}>
                        <span css={styles.selectChipLabel}>
                          {getOptionLabel(options.find((v) => String(v.id) === String(id)) ?? {id, label: id})}
                        </span>
                        <DlMuiDynamicIcon
                          iconName='haidiCloseIcon'
                          useFixedViewBox={16}
                          inputCss={styles.selectChipDeleteIconButton}
                          svgIconInputCss={styles.selectChipDeleteIcon}
                          onClick={multi ? chipOnDelete(id) : undefined}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          noButtonStyles
                        />
                      </div>
                    ))}
                    {selected?.length > 3 && (
                      <div css={styles.selectChip}>
                        <span css={styles.selectChipLabel} style={{maxWidth: '100%'}}>
                          ...
                        </span>
                      </div>
                    )}
                    {!hasValue && (
                      <span className='dlPlaceholder' css={styles.placeholder}>
                        {translatedPlaceholder}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {hasValue ? (
                      <span css={styles.selectChipLabel}>
                        {getOptionLabel(
                          options.find((v) => String(v.id) === String(selected)) ?? {
                            id: selected,
                            label: selected,
                          }
                        )}
                      </span>
                    ) : (
                      <span className='dlPlaceholder' css={styles.placeholder}>
                        {translatedPlaceholder}
                      </span>
                    )}
                  </>
                )}
              </Box>
            );
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {getOptionLabel(option)}
            </MenuItem>
          ))}
        </Select>
        {error?.bool && <FormHelperText>{error?.string}</FormHelperText>}
      </FormControl>
    </span>
  );
};

const getStyles: GetStylesFunction = (options) => {
  return {
    filterWrapper: css`
      max-width: 362px;
      width: 100%;
      min-height: 53px;
      display: flex;
      justify-content: align-start;
      align-items: center;
      padding: 0;
      & .MuiOutlinedInput-root {
        font-family: SourceSansPro-Regular;
        font-size: 14px;
        line-height: 18px;
        padding-right: 0;
      }
      & .MuiSelect-icon {
        top: calc(50% - 8px);
      }
      &:not(:hover, :focus) [data-name='select-clearAllIconButton'] {
        visibility: hidden;
      }
    `,
    select: css(
      `
      margin: 4px;
      width: 100%;
      `,
      options?.params?.selectCss
    ),
    textField: css({}),
    selectChip: css`
      height: 26px;
      padding: 4px 6px;
      border-radius: 4px;
      overflow: hidden;
      background: var(--dlTheme-100);
      font-family: var(--dlFontFamilySemiBold);
      display: inline-flex;
      align-items: center;
    `,
    selectChipLabel: css`
      font-family: var(--dlFontFamilyNormal);
      font-size: 14px;
      line-height: 18px;
      color: var(--dlTheme-800);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: inline-block;
      max-width: calc(100% - 24px);
    `,
    selectChipDeleteIconButton: css`
      margin-left: 8px;
      padding: 0;
    `,
    selectChipDeleteIcon: css`
      box-sizing: content-box;
      padding: 0;
      width: 16px;
      height: 16px;
      color: var(--dlThemeGray-900);
      &:hover {
        background: var(--dlTheme-300) !important;
      }
    `,
    placeholder: css`
      margin-top: 1px;
    `,
    clearAllIconButton: css`
      position: absolute;
      padding: 0;
      right: 28px;
      top: calc(50% - 8px);
    `,
    clearAllIcon: css`
      font-size: 17.5px;
      width: 1em;
      height: 1em;
    `,
  };
};
